import * as React from 'react';
import { useMemo } from 'react';

/** Material UI Packages. */
import { Typography, Tooltip, Autocomplete, TextField, Popper } from '@mui/material';
import { styled } from '@mui/material/styles';
import { GridActionsCellItem, useGridApiContext } from '@mui/x-data-grid-pro';

/** Material UI Icons. */
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

/** Internal Components. */
import { customized_style } from 'shared/config/Styles';
import MultilineNoteEditCell from 'shared/ui/MultilineNoteEditCell';

/**
 * Note 셀의 글자·여백 값. 보기 상태와 편집 상태가 이 한 벌을 똑같이 써야
 * 편집에 들어가고 나올 때 행 높이가 변하지 않는다(행이 `auto` 높이라 차이가 바로 보인다).
 * 여백은 `UpdateRateInfoTable` 의 셀 스타일에서 주고, 편집 셀 안쪽은 0 으로 둔다.
 */
export const NOTE_CELL_METRICS = { fontSize: '12px', lineHeight: 1.4, padY: '5px', padX: 1 };

/** Rate Info 다이얼로그의 Note 편집 셀 스타일. 안쪽 여백은 셀이 주므로 글자 값만 맞춘다. */
const NOTE_EDIT_CELL_SX = {
    '& .MuiInputBase-root': { fontSize: NOTE_CELL_METRICS.fontSize },
    '& .MuiInputBase-input': { fontSize: NOTE_CELL_METRICS.fontSize, lineHeight: NOTE_CELL_METRICS.lineHeight },
};

const StyledPopper = styled(Popper)({
    '& .MuiAutocomplete-listbox': { fontSize: '13px', maxHeight: '200px' },
    '& .MuiAutocomplete-option': { padding: '5px 5px' },
});

/** Autocomplete that stores option.id (integer FK) in the cell instead of the name string. */
function RateTypeAutocompleteCell({ params, options, lookupOptions, handleInputChange }) {
    const apiRef = useGridApiContext();

   const value = params.value;

    const matchedOption = useMemo(() => {
        if (value === null || value === undefined || value === '') return null;
        const byId = (list) => list?.find((o) => String(o.id) === String(value));
        return byId(options) ?? byId(lookupOptions) ?? null;
    }, [options, lookupOptions, value]);

    const optionId = matchedOption?.id ?? null;
    const optionName = matchedOption?.name ?? null;
    const currentOption = useMemo(
        () => (optionId == null ? null : { id: optionId, name: optionName }),
        [optionId, optionName]
    );

    const mergedOptions = useMemo(() => {
        if (!currentOption || options.some((o) => String(o.id) === String(currentOption.id))) return options;
        return [currentOption, ...options];
    }, [options, currentOption]);

    const handleChange = (_event, newValue) => {
        apiRef.current.setEditCellValue({ id: params.id, field: params.field, value: newValue?.id ?? null });
    };

    return (
        <Autocomplete
            value={currentOption}
            options={mergedOptions}
            getOptionLabel={(option) => option?.name ?? ''}
            isOptionEqualToValue={(option, val) => String(option.id) === String(typeof val === 'object' ? val?.id : val)}
            onChange={handleChange}
            onInputChange={handleInputChange}
            disableClearable
            fullWidth
            renderInput={(inputParams) => (
                <TextField
                    {...inputParams}
                    variant="standard"
                    autoFocus
                    sx={{ px: 1, '& .MuiInputBase-root': { border: 'none' } }}
                />
            )}
            renderOption={(props, option) => (
                <li {...props} key={option.id} style={{ margin: 0, padding: 0 }}>
                    {option.name}
                </li>
            )}
            slotProps={{ popper: { component: StyledPopper } }}
            sx={{
                height: '100%',
                width: '100%',
                '& .MuiInputBase-input': { fontSize: '13px' },
                '& .MuiInputBase-root': { p: 0, height: '100%' },
                '& .MuiFormControl-root': { height: '100%' },
            }}
        />
    );
}

/** Column definitions for DataGrid. */
export const getRateColumns = (
    rateType,
    rateTypeLookupList,
    handleRateChange,
    handleDeleteClick,
    onInvalidOption,
) => {
    const typesForLabel = rateTypeLookupList?.length ? rateTypeLookupList : rateType;
    const typeLabel = (raw) => {
        if (raw == null || raw === '') return '';
        const byId = typesForLabel.find((o) => String(o.id) === String(raw));
        if (byId) return byId.name;
        if (typeof raw === 'string' && Number.isNaN(Number(raw))) return raw;
        return '';
    };

    /**
     * Resolves an edited/pasted value (an option id from the Autocomplete cell, or a
     * pasted name string) to a valid rate-type id. Rejects anything that does not match
     * a known option so invalid values cannot be copied/pasted into the cell.
     */
    const resolveRateTypeId = (value, row) => {
        if (value === null || value === undefined || value === '') return null;
        const candidates = [...(rateTypeLookupList || []), ...(rateType || [])];
        const match =
            candidates.find((o) => String(o.id) === String(value)) ??
            candidates.find((o) => o.name === value);
        if (match) return match.id;
        onInvalidOption?.(value);
        return row?.il_rate_type_id ?? null;
    };

    return [
    { field: 'id', headerName: 'ID', hide: true },
    {
        field: 'il_rate_type_id',
        headerName: 'Type',
        align: 'center',
        headerAlign: 'center',
        editable: true,
        flex: 1,
        valueFormatter: (value) => typeLabel(value),
        valueParser: (value, row) => resolveRateTypeId(value, row),
        renderCell: (params) => (
            <Typography sx={{ ...customized_style.itemDatagrid_Typography, fontSize: 12 }}>
                {typeLabel(params.value)}
            </Typography>
        ),
        renderEditCell: (params) => (
            <RateTypeAutocompleteCell
                params={params}
                options={rateType}
                lookupOptions={typesForLabel}
                handleInputChange={(event, value, reason) => {
                    // Autocomplete also fires this with reason 'reset'/'clear' whenever its
                    // resolved `value` option changes identity (e.g. every render once the
                    // cell has a value, since `options` is a fresh array after each search).
                    // Only forward real typing, or a search-triggered re-render loop follows.
                    if (reason === 'input') handleRateChange(event, value);
                }}
            />
        ),
    },
    {
        field: 'rate',
        headerName: 'Rate',
        align: 'center',
        headerAlign: 'center',
        type: 'number',
        flex: 0.3,
        editable: true,
        valueParser: (value, row) => {
            if (value === null || value === undefined || value === '') return null;
            const numValue = Number(value);
            if (!Number.isFinite(numValue) || numValue < 0) return row?.rate ?? null;
            return numValue;
        },
        renderCell: (params) => (
            <Typography sx={{ ...customized_style.itemDatagrid_Typography, fontSize: 12 }}>
                {params.value ? `$${Number(params.value).toLocaleString()}` : ''}
            </Typography>
        ),
    },
    {
        field: 'note',
        headerName: 'Note',
        align: 'left',
        headerAlign: 'center',
        flex: 1,
        editable: true,
        // Wrapping (pre-wrap / break-word) is set on the cell in UpdateRateInfoTable and inherited here.
        renderCell: (params) => (
            <Typography sx={{ fontSize: NOTE_CELL_METRICS.fontSize, lineHeight: NOTE_CELL_METRICS.lineHeight }}>
                {params.value ?? ''}
            </Typography>
        ),
        renderEditCell: (params) => <MultilineNoteEditCell params={params} sx={NOTE_EDIT_CELL_SX} />,
    },
    {
        field: 'actions',
        type: 'actions',
        headerName: '',
        flex: 0.2,
        cellClassName: 'actions',
        getActions: ({ id }) => {
            return [
                <Tooltip title="Delete">
                    <GridActionsCellItem
                        icon={<DeleteIcon />}
                        label="Delete"
                        onClick={handleDeleteClick(id)}
                        color="inherit"
                    />
                </Tooltip>,
            ];
        },
    },
];
};
